import React, { useMemo, useState } from "react";
import { AlertTriangle, Check, Layers3, LockKeyhole, Minus, RefreshCw, ShieldCheck } from "lucide-react";
import { cn } from "../../lib/utils";

const CORE_MODULE_LABELS = Object.freeze({
  dashboard: "Dashboard",
  students: "Student records",
  admissions: "Admissions",
  registration: "Registration",
  onboarding: "School onboarding",
  attendance: "Attendance",
  timetable: "Timetable",
  exams: "Examinations",
  homework: "Homework",
  lessonplan: "Lesson plans",
  reportcards: "Report cards",
  achievements: "Achievements",
  communication: "Communication",
  ptm: "Parent-teacher meetings",
  discipline: "Discipline",
  transport: "Transport",
  website: "School website",
  fees: "Fee operations",
  compliance: "Compliance",
  tasks: "Tasks",
  forms: "Forms",
  settings: "Settings",
  help: "Help centre",
});

const CORE_GROUPS = Object.freeze([
  {
    id: "foundation",
    label: "School foundation",
    description: "The records and setup every school starts with.",
    ids: ["dashboard", "students", "admissions", "registration", "onboarding"],
  },
  {
    id: "academic",
    label: "Academic delivery",
    description: "Daily teaching, assessment and student progress.",
    ids: ["attendance", "timetable", "exams", "homework", "lessonplan", "reportcards", "achievements"],
  },
  {
    id: "community",
    label: "Community & campus",
    description: "Family communication and the school-day experience.",
    ids: ["communication", "ptm", "discipline", "transport", "website"],
  },
  {
    id: "administration",
    label: "Administration",
    description: "The controls that keep routine work organised.",
    ids: ["fees", "compliance", "tasks", "forms", "settings", "help"],
  },
]);

const FEATURE_GROUPS = Object.freeze([
  {
    id: "intelligence",
    label: "AI, automation & migration",
    shortLabel: "AI & automation",
    description: "Assisted work, governed automation and data transition.",
    ids: ["automation", "assistant", "insights", "migration"],
  },
  {
    id: "learning",
    label: "Digital learning & assessment",
    shortLabel: "Digital learning",
    description: "Classroom delivery and online examination capability.",
    ids: ["lms", "onlineexam"],
  },
  {
    id: "finance",
    label: "Finance, records & reporting",
    shortLabel: "Finance & records",
    description: "Financial controls and governed institutional outputs.",
    ids: ["accounting", "scholarship", "reports", "certificates"],
  },
  {
    id: "campus",
    label: "Campus services",
    shortLabel: "Campus services",
    description: "Operational depth beyond the always-on school core.",
    ids: ["alumni", "bustracking", "health", "hostel", "library", "frontoffice", "inventory"],
  },
  {
    id: "scale",
    label: "Scale & control",
    shortLabel: "Scale & control",
    description: "Identity, devices and multi-school governance.",
    ids: ["whitelabel", "biometric", "group_chain"],
  },
]);

const REQUIRED_PLAN_IDS = Object.freeze([
  "plan_trial",
  "plan_starter",
  "plan_growth",
  "plan_premium",
  "plan_enterprise",
]);

function humanizeId(id) {
  return String(id || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function uniqueById(items) {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function groupCatalogue(items, definitions, fallback) {
  const byId = new Map(items.map((item) => [item.id, item]));
  const claimed = new Set();
  const groups = definitions.map((definition) => {
    const groupedItems = definition.ids
      .map((id) => byId.get(id))
      .filter(Boolean);
    groupedItems.forEach((item) => claimed.add(item.id));
    return { ...definition, items: groupedItems };
  }).filter((group) => group.items.length > 0);

  const remaining = items.filter((item) => !claimed.has(item.id));
  if (remaining.length > 0) groups.push({ ...fallback, items: remaining });
  return groups;
}

/**
 * Shapes the live entitlement payload for presentation without inventing availability.
 * Unknown future ids remain visible in an explicit fallback group.
 */
export function buildPricingCatalogue(planMatrix) {
  const coreItems = uniqueById(
    (Array.isArray(planMatrix?.core_modules) ? planMatrix.core_modules : [])
      .filter((id) => typeof id === "string" && id.trim())
      .map((id) => ({ id, label: CORE_MODULE_LABELS[id] || humanizeId(id) })),
  );
  const features = uniqueById(
    (Array.isArray(planMatrix?.features) ? planMatrix.features : [])
      .filter((feature) => (
        feature
        && typeof feature.id === "string"
        && feature.id.trim()
        && typeof feature.label === "string"
        && feature.label.trim()
      ))
      .map((feature) => ({
        id: feature.id,
        label: feature.label,
        description: feature.description,
        dependencies: Array.isArray(feature.dependencies) ? feature.dependencies : [],
        requirement: typeof feature.requirement === "string" ? feature.requirement : "",
        readinessGated: feature.readiness_gated === true,
      })),
  );
  const releaseControlled = features.filter((feature) => feature.readinessGated);
  const sellable = features.filter((feature) => !feature.readinessGated);

  return {
    coreGroups: groupCatalogue(coreItems, CORE_GROUPS, {
      id: "other-core",
      label: "Additional core modules",
      description: "Additional always-included modules from the live catalogue.",
    }),
    featureGroups: groupCatalogue(sellable, FEATURE_GROUPS, {
      id: "other-features",
      label: "Additional plan features",
      shortLabel: "Additional",
      description: "More plan features from the live catalogue.",
    }),
    releaseControlled,
    counts: {
      core: coreItems.length,
      sellable: sellable.length,
      releaseControlled: releaseControlled.length,
    },
  };
}

export function validatePricingCatalogue(planMatrix, presentedPlans = null) {
  const errors = [];
  const coreModules = planMatrix?.core_modules;
  const features = planMatrix?.features;
  const plans = Array.isArray(presentedPlans) ? presentedPlans : planMatrix?.plans;

  if (!Array.isArray(coreModules) || coreModules.length === 0) {
    errors.push("core module catalogue is missing");
  } else if (
    !coreModules.every((id) => typeof id === "string" && id.trim())
    || new Set(coreModules).size !== coreModules.length
  ) {
    errors.push("core module catalogue is invalid or duplicated");
  }

  if (!Array.isArray(features) || features.length === 0) {
    errors.push("feature catalogue is missing");
  } else {
    const featureIds = features.map((feature) => feature?.id);
    const validFeatures = features.every((feature) => (
      feature
      && typeof feature.id === "string"
      && feature.id.trim()
      && typeof feature.label === "string"
      && feature.label.trim()
      && typeof feature.description === "string"
      && feature.description.trim()
      && (feature.dependencies === undefined || (
        Array.isArray(feature.dependencies)
        && feature.dependencies.every((dependency) => typeof dependency === "string" && dependency.trim())
      ))
      && (feature.requirement === undefined || typeof feature.requirement === "string")
      && typeof feature.readiness_gated === "boolean"
      && feature.default_state === (
        feature.readiness_gated
          ? "disabled_until_platform_enablement"
          : "plan_dependent"
      )
    ));
    if (!validFeatures || new Set(featureIds).size !== featureIds.length) {
      errors.push("feature catalogue is invalid, duplicated, or missing release-state metadata");
    }
  }

  if (!Array.isArray(plans) || plans.length === 0) {
    errors.push("plan rows are missing");
  } else {
    const planIds = plans.map((plan) => plan?.id);
    const featureIds = Array.isArray(features) ? features.map((feature) => feature?.id) : [];
    const featureIdSet = new Set(featureIds);
    const plansValid = plans.every((plan) => (
      plan
      && typeof plan.id === "string"
      && plan.id.trim()
      && typeof plan.name === "string"
      && plan.name.trim()
      && plan.features
      && typeof plan.features === "object"
      && !Array.isArray(plan.features)
      && Array.isArray(features)
      && features.every((feature) => typeof plan.features[feature?.id] === "boolean")
      && Object.keys(plan.features).length === featureIdSet.size
      && Object.keys(plan.features).every((featureId) => featureIdSet.has(featureId))
    ));
    const hasRequiredPlans = REQUIRED_PLAN_IDS.every((planId) => planIds.includes(planId));
    if (
      !plansValid
      || new Set(planIds).size !== planIds.length
      || !hasRequiredPlans
    ) {
      errors.push("plan rows are invalid, duplicated, or incomplete");
    }
  }

  return { valid: errors.length === 0, errors };
}

// WP-03: a bare "Not included" in a paid column beside an "Included" trial cell reads as a
// downgrade warning. When the trial has the feature, the paid cell names the lowest tier that
// keeps it on — turning the same fact into an upgrade path.
function Availability({ included, unlockTier }) {
  if (included) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-positive/10 px-2 py-1 text-[11px] font-semibold text-positive">
        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> Included
      </span>
    );
  }
  if (unlockTier) {
    return (
      <span className="inline-block max-w-[130px] text-[11px] font-medium leading-tight text-ink-subtle">
        Trial only — requires <span className="font-semibold text-ink-2">{unlockTier}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-medium text-ink-subtle">
      <Minus className="h-3 w-3" aria-hidden="true" /> Not included
    </span>
  );
}

// Lowest PAID tier that includes the feature. Ranked by the KNOWN tier ladder, never the served
// array order — a reordered plans payload must not flip the "requires X" claim to a higher tier.
// Unknown plan ids keep their served position after the known ladder (stable sort).
const PAID_TIER_RANK = { plan_starter: 0, plan_growth: 1, plan_premium: 2, plan_enterprise: 3 };
function lowestPaidTierWith(featureId, plans) {
  const hit = (plans || [])
    .filter((plan) => plan?.id !== "plan_trial" && plan?.features?.[featureId] === true)
    .sort((a, b) => (PAID_TIER_RANK[a?.id] ?? 99) - (PAID_TIER_RANK[b?.id] ?? 99))[0];
  return hit ? hit.name : null;
}

export default function PricingFeatureCatalogue({ onRetry, planMatrix, plans = null }) {
  const catalogue = useMemo(() => buildPricingCatalogue(planMatrix), [planMatrix]);
  const resolvedPlans = useMemo(
    () => (Array.isArray(plans) && plans.length > 0 ? plans : (planMatrix?.plans || [])),
    [planMatrix, plans],
  );
  const validation = useMemo(
    () => validatePricingCatalogue(planMatrix, resolvedPlans),
    [planMatrix, resolvedPlans],
  );
  // Hoisted once per data change: which trial-included features unlock at which paid tier.
  // The per-cell lookups below stay O(1) instead of re-scanning plans for every table cell.
  const unlockTierByFeature = useMemo(() => {
    const trialFeatures = resolvedPlans.find((p) => p?.id === "plan_trial")?.features || {};
    const map = {};
    for (const group of catalogue.featureGroups) {
      for (const feature of group.items || []) {
        if (trialFeatures[feature.id] === true) map[feature.id] = lowestPaidTierWith(feature.id, resolvedPlans);
      }
    }
    return map;
  }, [catalogue, resolvedPlans]);
  const [activeGroup, setActiveGroup] = useState("all");
  const visibleGroups = activeGroup === "all"
    ? catalogue.featureGroups
    : catalogue.featureGroups.filter((group) => group.id === activeGroup);

  if (!validation.valid) {
    return (
      <div className="rounded-2xl border border-critical/30 bg-critical-soft p-5 sm:p-6" data-testid="pricing-catalogue-error" role="alert">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-critical" aria-hidden="true" />
          <div>
            <h3 className="font-heading text-lg font-semibold text-ink">Plan catalogue is unavailable</h3>
            <p className="mt-1 text-sm leading-relaxed text-ink-2">The current plan data is incomplete, so missing values are not being presented as plan exclusions.</p>
            {onRetry ? (
              <button className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2 text-sm font-semibold text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={onRetry} type="button">
                <RefreshCw className="h-4 w-4" aria-hidden="true" /> Reload catalogue
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="pricing-feature-catalogue">
      <dl className="grid overflow-hidden rounded-2xl border border-line bg-surface sm:grid-cols-3" aria-label="Catalogue summary">
        {[
          [catalogue.counts.core, "Always-on core", "Included in every plan"],
          [catalogue.counts.sellable, "Plan-specific features", "Compared below"],
          [catalogue.counts.releaseControlled, "Still rolling out", "Separated from tier comparison"],
        ].map(([value, label, detail], index) => (
          <div className={cn("p-5", index > 0 && "border-t border-line sm:border-l sm:border-t-0")} key={label}>
            <dt className="text-xs font-semibold uppercase tracking-[0.13em] text-ink-subtle">{label}</dt>
            <dd className="mt-2 flex items-end gap-2">
              <span className="font-heading text-3xl font-bold tabular-nums tracking-tight text-ink">{value}</span>
              <span className="pb-1 text-xs text-ink-subtle">{detail}</span>
            </dd>
          </div>
        ))}
      </dl>

      <section aria-labelledby="pricing-core-heading">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">Every plan starts here</p>
            <h3 className="mt-1 font-heading text-2xl font-bold tracking-tight text-ink" id="pricing-core-heading">Always-on school operations</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink-2">These modules come from the live core catalogue and do not depend on a paid feature gate.</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-muted px-3 py-1.5 text-xs font-semibold text-ink-2">
            <Layers3 className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> {catalogue.counts.core} modules
          </span>
        </div>
        <div className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4" data-testid="pricing-core-groups">
          {catalogue.coreGroups.map((group) => (
            <article className="bg-surface p-5" key={group.id}>
              <h4 className="font-heading text-[15px] font-semibold text-ink">{group.label}</h4>
              <p className="mt-1 min-h-10 text-xs leading-relaxed text-ink-subtle">{group.description}</p>
              <ul className="mt-4 space-y-2.5">
                {group.items.map((module) => (
                  <li className="flex items-start gap-2 text-[13px] leading-snug text-ink-2" data-core-module-id={module.id} key={module.id}>
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2.5} aria-hidden="true" />
                    <span>{module.label}<span className="sr-only"> — Included in every plan</span></span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="pricing-plan-features-heading">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">What's included on each plan</p>
          <h3 className="mt-1 font-heading text-2xl font-bold tracking-tight text-ink" id="pricing-plan-features-heading">Plan-specific features</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-2" data-testid="pricing-trial-note">
            Your 90-day trial includes every feature in this comparison. The columns below show what
            stays on after you choose a plan — every cell reflects the live plan configuration, not an estimate.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" aria-label="Filter feature groups" role="group">
          <button
            aria-controls="pricing-feature-comparison"
            aria-pressed={activeGroup === "all"}
            className={cn(
              "rounded-full border px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              activeGroup === "all" ? "border-primary bg-primary text-primary-fg" : "border-line bg-surface text-ink-2 hover:border-primary/40 hover:text-ink",
            )}
            onClick={() => setActiveGroup("all")}
            type="button"
          >
            All groups · {catalogue.counts.sellable}
          </button>
          {catalogue.featureGroups.map((group) => (
            <button
              aria-controls="pricing-feature-comparison"
              aria-pressed={activeGroup === group.id}
              className={cn(
                "rounded-full border px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                activeGroup === group.id ? "border-primary bg-primary text-primary-fg" : "border-line bg-surface text-ink-2 hover:border-primary/40 hover:text-ink",
              )}
              key={group.id}
              onClick={() => setActiveGroup(group.id)}
              type="button"
            >
              {group.shortLabel || group.label} · {group.items.length}
            </button>
          ))}
        </div>
        <p className="sr-only" role="status">Showing {activeGroup === "all" ? "all feature groups" : visibleGroups[0]?.label}.</p>

        <p className="mt-5 text-xs text-ink-subtle md:hidden">Swipe horizontally and scroll vertically to compare every plan.</p>
        <div
          aria-label="Plan feature comparison, scrollable"
          className="mt-2 max-h-[min(72vh,52rem)] overflow-auto overscroll-x-contain rounded-2xl border border-line bg-surface [contain:layout_inline-size] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          data-testid="pricing-page-matrix"
          id="pricing-feature-comparison"
          role="region"
          tabIndex={0}
        >
          <table className="w-full min-w-[880px] border-separate border-spacing-0 text-sm">
            <caption className="sr-only">Plan-specific feature availability by plan, grouped by capability</caption>
            <thead className="sticky top-0 z-30">
              <tr>
                <th className="sticky left-0 z-40 w-[190px] border-b border-r border-line bg-muted px-4 py-3 text-left font-semibold text-ink sm:w-[250px]" scope="col">Feature</th>
                {resolvedPlans.map((plan) => (
                  <th className="min-w-[124px] border-b border-line bg-muted px-3 py-3 text-center" key={plan.id} scope="col">
                    <span className={cn("block font-heading font-bold", plan.popular ? "text-primary" : "text-ink")}>{plan.name}</span>
                    {plan.popular ? <span className="block text-[10px] font-semibold uppercase tracking-wide text-primary">Most popular</span> : null}
                    {plan.trialFullProduct ? (
                      <span className="mt-0.5 block text-[11px] font-semibold text-positive">Every feature below · 90 days</span>
                    ) : (
                      <span className="mt-0.5 block text-[11px] font-normal text-ink-subtle">{plan.price}{plan.period}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            {visibleGroups.map((group) => (
              <tbody aria-labelledby={`pricing-feature-group-${group.id}`} key={group.id}>
                <tr>
                  <th className="sticky left-0 z-10 border-b border-r border-line bg-primary/[0.06] px-4 py-3 text-left" colSpan={1} id={`pricing-feature-group-${group.id}`} scope="rowgroup">
                    <span className="block text-[12px] font-bold uppercase tracking-[0.1em] text-primary">{group.label}</span>
                    <span className="mt-0.5 block text-[11px] font-normal normal-case tracking-normal text-ink-subtle">{group.description}</span>
                  </th>
                  <td className="border-b border-line bg-primary/[0.06]" colSpan={Math.max(resolvedPlans.length, 1)} />
                </tr>
                {group.items.map((feature, index) => (
                  <tr className={index % 2 === 1 ? "bg-muted/35" : "bg-surface"} key={feature.id}>
                    <th className={cn(
                      "sticky left-0 z-10 border-r border-line px-4 py-3 text-left text-[13px] font-medium text-ink-2",
                      index % 2 === 1 ? "bg-muted" : "bg-surface",
                    )} data-feature-id={feature.id} scope="row">
                      <span className="block">{feature.label}</span>
                      <span className="mt-1 block text-[11px] font-normal leading-snug text-ink-subtle">{feature.description}</span>
                      {feature.dependencies.length > 0 ? (
                        <span className="mt-1 block text-[10.5px] font-medium leading-snug text-primary">Requires {feature.dependencies.map(humanizeId).join(" + ")}</span>
                      ) : null}
                      {feature.requirement ? (
                        <span className="mt-1 block text-[10.5px] font-medium leading-snug text-caution">Prerequisite: {feature.requirement}</span>
                      ) : null}
                    </th>
                    {resolvedPlans.map((plan) => {
                      const included = plan?.features?.[feature.id] === true;
                      const unlockTier = !included && plan?.id !== "plan_trial"
                        ? unlockTierByFeature[feature.id] || null
                        : null;
                      return (
                        <td className="border-b border-line px-3 py-3 text-center" key={plan.id}>
                          <Availability included={included} unlockTier={unlockTier} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </section>

      {catalogue.releaseControlled.length > 0 ? (
        <section className="overflow-hidden rounded-2xl border border-caution/30 bg-caution-soft" aria-labelledby="pricing-release-heading" data-testid="pricing-release-controlled">
          <div className="flex items-start gap-3 border-b border-caution/25 p-5 sm:p-6">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface text-caution shadow-sm">
              <LockKeyhole className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-caution">Separate from purchasable tiers</p>
              <h3 className="mt-1 font-heading text-xl font-bold tracking-tight text-ink" id="pricing-release-heading">Capabilities still rolling out</h3>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-2">These capabilities switch on school by school as they finish rolling out. They are listed for transparency — not sold as standard plan inclusions yet.</p>
            </div>
          </div>
          <ul className="divide-y divide-caution/20 bg-surface/70">
            {catalogue.releaseControlled.map((feature) => (
              <li className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6" data-feature-id={feature.id} key={feature.id}>
                <span className="flex min-w-0 items-start gap-2.5 text-sm font-medium text-ink">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-caution" aria-hidden="true" />
                  <span>
                    <span className="block">{feature.label}</span>
                    <span className="mt-1 block max-w-2xl text-xs font-normal leading-relaxed text-ink-2">{feature.description}</span>
                    {feature.dependencies.length > 0 ? <span className="mt-1 block text-[11px] font-medium text-caution">Requires {feature.dependencies.map(humanizeId).join(" + ")}</span> : null}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-caution/30 bg-caution-soft px-2.5 py-1 text-[11px] font-semibold text-ink-2">
                  <LockKeyhole className="h-3 w-3 text-caution" aria-hidden="true" /> Still rolling out
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
